import React, { useEffect, useState } from "react"
import { FiPlusCircle, FiEye } from "react-icons/fi"
import Spinner from "../../components/Spinner"
import DashboardHeader from "../../components/DashboardHeader"
import FilterWithSearchAndRegion from "../../components/Filters/FilterWithSearchAndRegion"
import { Tooltip } from "../../layout/sidebar"
import { RxPencil1 } from "react-icons/rx"
import useSWR from "swr"
import { fetchAll } from "../../utils/api"
import { SuccessNotification, ErrorNotification } from "../../utils"
import ServiceProviderDetailsModal from "../../components/modals/ServiceProviderDetailsModal"
import AddServiceProviderModal from "../../components/modals/AddServiceProviderModal"
import DeleteProcessorModal from "../../components/modals/DeleteProcessorModal"
import RenderNoProvider from "../../components/NoData/RenderNoProvider"
import { useHistory } from "react-router-dom"
import Pagination from "../../components/Pagination"

const Index = () => {
  const history = useHistory()
  const [search, setSearch] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(10)
  const [view, setView] = useState("list")
  const [selected, setSelected] = useState(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const initialState = {
    region: "",
    district: "",
    community: "",
  }
  const [states, setState] = useState(initialState)

  const { data: providers, isLoading, mutate } = useSWR(
    "service_providers/",
    () => fetchAll("service_providers/")
  )

  const handleChange = (evt) => {
    setState({
      ...states,
      [evt.target.name]: evt.target.value,
    })
    setCurrentPage(1)
  }

  const resetFilters = () => {
    setSearch("")
    setState(initialState)
    setCurrentPage(1)
  }

  const getFilteredProviders = () => {
    let data = providers || []
    if (search) {
      const q = search.toLowerCase()
      data = data.filter(p =>
        (p.service_provider?.firstname || "").toLowerCase().includes(q) ||
        (p.service_provider?.lastname || "").toLowerCase().includes(q) ||
        (p.service_provider?.business_name || "").toLowerCase().includes(q) ||
        (p.service_provider?.email || "").toLowerCase().includes(q) ||
        (p.service_provider?.phone || "").toLowerCase().includes(q)
      )
    }
    if (states.region && states.region !== "Select Region") {
      data = data.filter(p => (p.service_provider?.region || "").toLowerCase().includes(states.region.toLowerCase()))
    }
    if (states.district && states.district !== "Select District") {
      data = data.filter(p => (p.service_provider?.district || "").toLowerCase().includes(states.district.toLowerCase()))
    }
    if (states.community && states.community !== "Select Community") {
      data = data.filter(p => (p.service_provider?.community || "").toLowerCase().includes(states.community.toLowerCase()))
    }
    return data
  }

  const filteredProviders = getFilteredProviders()
  const totalPages = Math.ceil(filteredProviders.length / pageSize)
  const paginatedProviders = filteredProviders.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1)
    }
  }, [filteredProviders.length])

  const handlePageClick = (page) => {
    setCurrentPage(page)
  }

  const handleDeleteServiceProvider = async () => {
    if (!selected?.id) return
    setDeleteLoading(true)
    try {
      // Logic for delete here...
      setDeleteLoading(false)
      setShowDeleteModal(false)
      setShowDetailsModal(false)
      mutate()
      SuccessNotification("Service Provider deleted successfully")
    } catch (error) {
      setDeleteLoading(false)
      ErrorNotification("Failed to delete")
    }
  }

  const suggestions = Array.from(new Set((providers || []).map(p =>
    p.service_provider?.is_business_account ? p.service_provider?.business_name : `${p.service_provider?.firstname} ${p.service_provider?.lastname}`
  ).filter(Boolean)))

  return (
    <>
      {showAddModal && <AddServiceProviderModal
        showAddModal={showAddModal}
        setShowAddModal={setShowAddModal}
        mutate={mutate}
      />}
      {showDetailsModal && <ServiceProviderDetailsModal
        showDetailsModal={showDetailsModal}
        setShowDetailsModal={setShowDetailsModal}
        selected={selected}
        loading={deleteLoading}
        onDelete={handleDeleteServiceProvider}
      />}
      {showDeleteModal && <DeleteProcessorModal
        setShowDeleteModal={setShowDeleteModal}
        loading={deleteLoading}
        onDelete={handleDeleteServiceProvider}
        label="service provider"
      />}

      <div className="w-full lg:px-10 px-2 h-full py-10">
        <div className="w-full space-y-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between md:flex-row md:items-center md:justify-between mb-4">
            <DashboardHeader label="Service Providers" />
            <div className="flex flex-col md:flex-row gap-2">
              <button
                onClick={() => {
                  setShowAddModal(true)
                }}
                className="bg-yellowcolor hover:bg-yellowcolor/80 text-black px-6 flex flex-row items-center capitalize gap-2 py-2 rounded-md"
              >
                <FiPlusCircle size={20} />
                Add new Service Provider
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <FilterWithSearchAndRegion
              placeholder="Search service provider"
              search={search}
              setSearch={value => setSearch(value)}
              onSearch={value => {
                setSearch(value)
                setCurrentPage(1)
              }}
              view={view}
              suggestions={suggestions}
              setView={setView}
              handleChange={handleChange}
              hideMap={true}
            />
            {(search || states.region || states.district || states.community) && (
              <div className="flex justify-end">
                <button
                  onClick={resetFilters}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm"
                >
                  Reset Filters
                </button>
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="h-96 grid items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <>
              <div className="my-5">
                {filteredProviders.length > 0 ? (
                  <div className="my-5">
                    <div className="flex flex-col">
                      <div className="-m-1.5 overflow-x-auto">
                        <div className="p-1.5 min-w-full inline-block align-middle">
                          <div className="bg-white dark:bg-dark border dark:border-border-dark dark:text-white bg-clip-border rounded-lg shadow-sm overflow-hidden ">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-border-dark">
                              <thead className="bg-transparent dark:bg-secondaryDark">
                                <tr>
                                  <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500  capitalize hover:text-border-dark dark:hover:text-border-dark  ">Service Provider</th>
                                  <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark ">Phone / Email</th>
                                  <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark ">Region</th>
                                  <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark ">District</th>
                                  <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark ">Community</th>
                                  <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark ">Status</th>
                                  <th scope="col" className="px-6 py-3 text-end text-sm font-light text-gray-500  capitalize hover:text-black ">Action</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y bg-white divide-gray-200 dark:bg-dark dark:divide-border-dark">
                                {paginatedProviders?.map((data) => (
                                  <tr key={data.id} className="text-[13px]">
                                    <td className="px-6 hover:bg-gray-50 dark:hover:bg-secondaryDark py-4 whitespace-nowrap text-sm font-medium text-gray-800 dark:text-neutral-200 flex flex-row items-center gap-2 flex-1">
                                      {data?.service_provider?.profile_image ? <div className="w-10 h-10 overflow-hidden shrink-0">
                                        <img
                                          src={data?.service_provider?.profile_image}
                                          alt=""
                                          className="w-full h-full object-cover rounded-full" />
                                      </div> : <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200 shrink-0">
                                        <p className="text-sm uppercase font-bold text-gray-600">
                                          {data?.service_provider?.firstname?.charAt(0)}{data?.service_provider?.lastname?.charAt(0)}
                                        </p>
                                      </div>}
                                      <p>
                                        {data?.service_provider?.is_business_account ? data?.service_provider?.business_name : `${data?.service_provider?.firstname} ${data?.service_provider?.lastname}` || "N/A"}
                                      </p>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="flex flex-col">
                                        <p className="capitalize">
                                          {data?.service_provider?.phone || "N/A"}
                                        </p>
                                        <p className="lowercase text-xs">
                                          {data?.service_provider?.email || "N/A"}
                                        </p>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      {data?.service_provider?.region || "N/A"}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      {data?.service_provider?.district || "N/A"}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap font-light">
                                      {data?.service_provider?.community || "N/A"}
                                    </td>
                                    <td className="px-6 py-4 capitalize whitespace-nowrap font-light">
                                      {data?.is_active ? "Active" : "Not active" || "N/A"}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-end font-light">
                                      <div className="flex flex-row items-center gap-4">
                                        <Tooltip text="View" position="top">
                                          <button
                                            onClick={() => {
                                              setSelected(data)
                                              setShowDetailsModal(true)
                                            }}
                                            className="">
                                            <FiEye size={16} />
                                          </button>
                                        </Tooltip>
                                        <Tooltip text="Update" position="top">
                                          <button onClick={() => {
                                            history.push("/admin/updateAggregator", { aggregator: data })
                                          }} className="">
                                            <RxPencil1 size={16} />
                                          </button>
                                        </Tooltip>
                                        <Tooltip text="Delete" position="top">
                                          <button
                                            onClick={() => {
                                              setSelected(data)
                                              setShowDeleteModal(true)
                                            }}
                                            className="">
                                            <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                              <path d="M18 4.98307C15.225 4.70807 12.4333 4.56641 9.65 4.56641C8 4.56641 6.35 4.64974 4.7 4.81641L3 4.98307" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M7.58398 4.14102L7.76732 3.04935C7.90065 2.25768 8.00065 1.66602 9.40898 1.66602H11.5923C13.0007 1.66602 13.109 2.29102 13.234 3.05768L13.4173 4.14102" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M16.2077 7.61523L15.666 16.0069C15.5744 17.3152 15.4993 18.3319 13.1743 18.3319H7.82435C5.49935 18.3319 5.42435 17.3152 5.33268 16.0069L4.79102 7.61523" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M9.10742 13.75H11.8824" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M8.41602 10.416H12.5827" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                          </button>
                                        </Tooltip>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <Pagination
                              currentPage={currentPage}
                              totalPages={totalPages}
                              onPageChange={handlePageClick}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <RenderNoProvider
                    isSearch={search?.trim()?.length > 0}
                    handleNewGroup={() => {
                      history.push("/admin/newAggregator")
                    }} />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default Index
